import { useMemo, useState } from "react";
import { PageRenderer } from "@/components/sections/PageRenderer";
import { SectionTitle } from "@/components/common/SectionTitle";
import { TestimonialCard } from "@/components/cards/TestimonialCard";
import { RedBand } from "@/components/common/RedBand";
import { AsyncState } from "@/components/common/Loader";
import { usePublicContent } from "@/lib/useContent";
import { cn } from "@/lib/utils";

const FILTERS = [
  "All",
  "Youth Athletes",
  "Volleyball Athletes",
  "College Athletes",
  "Adult Fitness Clients",
  "Team Results",
  "Transformation Stories",
];

export default function Testimonials() { return <PageRenderer pageKey="testimonials" />; }

export const TestimonialsBody = () => {
  const { data, loading } = usePublicContent("testimonials");
  const [active, setActive] = useState("All");

  const filtered = useMemo(() => {
    if (active === "All") return data;
    return data.filter((t) => t.category === active);
  }, [data, active]);

  return (
    <div data-testid="testimonials-page">
      {/* hero via PageRenderer */}

      <section id="testimonials-list" className="scroll-mt-24 bg-background py-16 sm:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <SectionTitle eyebrow="Success Stories" title="Highlight Athletes & Clients" subtitle="Filter by category to see results across our community." />

          <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Testimonial categories" data-testid="testimonial-filter-tabs">
            {FILTERS.map((f) => {
              const isActive = active === f;
              return (
                <button
                  key={f}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(f)}
                  data-testid={`testimonial-filter-${f.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                  className={cn(
                    "rounded-full border px-4 py-2 font-condensed text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-200 active:scale-[0.98]",
                    isActive ? "border-[hsl(var(--brand-red))] bg-brand text-white" : "border-border bg-transparent text-muted-foreground hover:border-[hsl(var(--brand-red))] hover:text-foreground"
                  )}
                >
                  {f}
                </button>
              );
            })}
          </div>

          <div className="mt-10">
            <AsyncState loading={loading} empty={filtered.length === 0} emptyTitle="No stories in this category yet" emptyDescription="Try a different filter.">
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((t) => <TestimonialCard key={t.id} item={t} />)}
              </div>
            </AsyncState>
          </div>
        </div>
      </section>

      <div id="testimonials-cta" className="scroll-mt-24">
      <RedBand
        eyebrow="Your Turn"
        title="Start Your Success Story"
        text="Book a free intro workout and take the first step toward your goals."
        primary={{ label: "Book Intro", to: "/booking" }}
        secondary={{ label: "View Packages", to: "/packages" }}
        testid="testimonials-cta"
      />
      </div>
    </div>
  );
}
